import React, { useEffect, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import classes from './styles.module.scss';
import { actionNotifier } from '../../components/ui/toast';

const createDefaultPaper = () => ({
    code: '',
    marks: '',
    title: '',
    subTitle: '',
});

const normalizeCutOffList = (list) => (
    (list || []).map((group) => ({
        id: group?.id || '',
        title: group?.title || '',
        papers: (group?.papers || []).map((paper) => ({
            code: paper?.code || '',
            marks: paper?.marks ?? '',
            title: paper?.title || '',
            subTitle: paper?.subTitle || '',
        })),
    }))
);

const SiteCutOffContainer = ({ cutOffList, isLoading, onSave, refetchCutOffList }) => {
    const [formData, setFormData] = useState([]);

    useEffect(() => {
        setFormData(normalizeCutOffList(cutOffList));
    }, [cutOffList]);

    const handleGroupChange = (groupIndex, field, value) => {
        setFormData((prevState) => prevState.map((group, index) => (
            index === groupIndex ? { ...group, [field]: value } : group
        )));
    };

    const handlePaperChange = (groupIndex, paperIndex, field, value) => {
        setFormData((prevState) => prevState.map((group, index) => {
            if (index !== groupIndex) {
                return group;
            }

            return {
                ...group,
                papers: group.papers.map((paper, innerIndex) => (
                    innerIndex === paperIndex ? { ...paper, [field]: value } : paper
                )),
            };
        }));
    };

    const handleAddPaper = (groupIndex) => {
        setFormData((prevState) => prevState.map((group, index) => (
            index === groupIndex
                ? { ...group, papers: [...group.papers, createDefaultPaper()] }
                : group
        )));
    };

    const handleRemovePaper = (groupIndex, paperIndex) => {
        setFormData((prevState) => prevState.map((group, index) => {
            if (index !== groupIndex) {
                return group;
            }

            return {
                ...group,
                papers: group.papers.filter((_, innerIndex) => innerIndex !== paperIndex),
            };
        }));
    };

    const handleSave = async () => {
        const payload = formData.map((group) => ({
            id: group.id.trim(),
            title: group.title.trim(),
            papers: group.papers.map((paper) => ({
                code: paper.code.trim(),
                marks: Number(paper.marks) || 0,
                title: paper.title.trim(),
                subTitle: paper.subTitle.trim(),
            })),
        }));

        const isInvalid = payload.some((group) => (
            !group.id || !group.title || group.papers.some((paper) => !paper.code || !paper.title)
        ));

        if (isInvalid) {
            actionNotifier.error('Please fill all required cut off fields before saving.');
            return;
        }

        try {
            await onSave(payload);
        } catch (error) {
            actionNotifier.error(error?.data?.message || 'Unable to save cut off data.');
        }
    };

    return (
        <div className={classes.cutoff_page_container}>
            <div className={classes.page_header}>
                <button
                    type="button"
                    className={classes.refresh_btn}
                    onClick={refetchCutOffList}
                >
                    <RefreshIcon fontSize="small" /> Refresh
                </button>

                <button
                    type="button"
                    className={classes.save_btn}
                    onClick={handleSave}
                    disabled={isLoading}
                >
                    <SaveIcon fontSize="small" /> {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            <div className={classes.section_list}>
                {formData.length > 0 ? formData.map((group, groupIndex) => (
                    <div className={classes.group_card} key={group.id || groupIndex}>
                        <div className={classes.group_header}>
                            <div className={classes.group_fields}>
                                <div className={classes.form_group}>
                                    <label>Group ID *</label>
                                    <input
                                        type="text"
                                        value={group.id}
                                        onChange={(event) => handleGroupChange(groupIndex, 'id', event.target.value)}
                                    />
                                </div>
                                <div className={classes.form_group}>
                                    <label>Title *</label>
                                    <input
                                        type="text"
                                        value={group.title}
                                        onChange={(event) => handleGroupChange(groupIndex, 'title', event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={classes.papers_table_wrapper}>
                            <table className={classes.papers_table}>
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Marks</th>
                                        <th>Title</th>
                                        <th>Sub Title</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.papers.map((paper, paperIndex) => (
                                        <tr key={`${group.id || groupIndex}-${paperIndex}`}>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={paper.code}
                                                    onChange={(event) => handlePaperChange(groupIndex, paperIndex, 'code', event.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={paper.marks}
                                                    onChange={(event) => handlePaperChange(groupIndex, paperIndex, 'marks', event.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={paper.title}
                                                    onChange={(event) => handlePaperChange(groupIndex, paperIndex, 'title', event.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={paper.subTitle}
                                                    onChange={(event) => handlePaperChange(groupIndex, paperIndex, 'subTitle', event.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={classes.remove_btn}
                                                    onClick={() => handleRemovePaper(groupIndex, paperIndex)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={classes.group_actions}>
                            <button
                                type="button"
                                className={classes.add_btn}
                                onClick={() => handleAddPaper(groupIndex)}
                            >
                                Add Paper
                            </button>
                        </div>
                    </div>
                )) : (
                    <div className={classes.empty_state}>
                        No cut off data found.
                    </div>
                )}
            </div>
        </div>
    );
};

export default SiteCutOffContainer;
